import { ArrowLeftOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu, message, Space, Typography } from "antd";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { ProjectContext } from "context/ProjectProvider";
import { downloadFileInBrowser } from "utils/utils";
import moment from "moment";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import { useCustomReports } from "../context/custom-reports-provider";
import { ContextReportDetail } from "../models";
import { exportCustomReportAsCSV } from "../services/custom-report-services";

const { Paragraph } = Typography;
const reportDetailsPlaceholder = "Enter Report Description";

enum ExportCSVFormat {
  FORMATTED = "formatted", // flat_layout: false
  WITHOUT_FORMATTING = "without_formatting" // flat_layout: true
}

interface CustomReportsPageHeaderProps {
  disable?: boolean;
  onUpdateProjectReport: (
    data: any,
    showSuccessMessage?: boolean
  ) => Promise<void>;
  showExportOptions?: boolean;
}
function ReportsPageHeader({
  disable = false,
  onUpdateProjectReport,
  showExportOptions = false
}: CustomReportsPageHeaderProps) {
  const history = useHistory();
  const { projectId } = useParams() as any;

  const { gridStatusBudge, detail } = useCustomReports();

  const { projectToken, projectDetails } = useContext(ProjectContext);

  const [reportDetail, setReportDetail] = useState<ContextReportDetail | null>(
    detail
  );

  const [isDownloadingExportedCSV, setIsDownloadingExportedCSV] =
    useState(false);

  useEffect(() => {
    setReportDetail(detail);
  }, [detail]);

  const backButton = () => {
    history.push(`/project/${projectId}/reports`);
  };

  const onExportCSV = useCallback(
    async (key?: any) => {
      if (isDownloadingExportedCSV) {
        return;
      }
      setIsDownloadingExportedCSV(true);
      const payload = {
        flat_layout: key === ExportCSVFormat.WITHOUT_FORMATTING
      };
      const response = await exportCustomReportAsCSV(
        reportDetail?.id!,
        projectToken,
        payload
      );

      if (!response.success) {
        setIsDownloadingExportedCSV(false);
        message.error(
          response.errorMsg || "An error occurred while exporting the report."
        );
        return;
      }

      if (response.data?.url) {
        downloadFileInBrowser(
          response.data.url,
          `${projectDetails?.name}_${reportDetail?.name}_${moment().format(
            DATE_FORMAT_MMDDYYYY
          )}.csv`,
          () => {
            setIsDownloadingExportedCSV(false);
          }
        );
      } else {
        setIsDownloadingExportedCSV(false);
      }
    },
    [
      isDownloadingExportedCSV,
      reportDetail?.id,
      reportDetail?.name,
      projectToken,
      projectDetails?.name
    ]
  );

  const exportBtnMenu = useMemo(
    () => (
      <Menu onClick={(info) => onExportCSV(info.key)}>
        <Menu.Item key={ExportCSVFormat.FORMATTED}>
          <div className="py-1">
            <div className="font-medium text-sm text-ciq-black-E0">
              Formatted
            </div>
            <div className="text-xs text-ciq-gray-700">
              Cleaner format as seen in-app.
            </div>
          </div>
        </Menu.Item>
        <Menu.Item key={ExportCSVFormat.WITHOUT_FORMATTING}>
          <div className="py-1">
            <div className="font-medium text-sm">Without Formatting</div>
            <div className="text-xs text-ciq-gray-700">
              Repeated data to avoid blank cells.
            </div>
          </div>
        </Menu.Item>
      </Menu>
    ),
    [onExportCSV]
  );

  return (
    <div
      className="w-full px-6 py-4 flex justify-between gap-x-6 -mb-4"
      data-testid="custom-report-header-container"
    >
      {/* Left Section */}
      <div className="grow flex space-x-4">
        {/* Back Arrow */}
        <div className="flex items-center">
          <ArrowLeftOutlined
            className="text-black text-base cursor-pointer mb-2"
            onClick={backButton}
            data-testid="custom-report-back-button"
          />
        </div>

        {/* Title + Description */}
        {reportDetail && (
          <div className="grow flex flex-col">
            <div
              className="flex space-x-4 items-center"
              data-testid="custom-report-title-section"
            >
              <Typography.Title
                style={{ margin: 0 }}
                level={5}
                editable={
                  disable
                    ? false
                    : {
                        onChange: (newValue) => {
                          setReportDetail((pre) =>
                            pre
                              ? {
                                  ...pre,
                                  name: newValue
                                }
                              : null
                          );
                          onUpdateProjectReport({ name: newValue }, true);
                        },
                        triggerType: ["text", "icon"]
                      }
                }
                data-testid="custom-report-title"
              >
                {reportDetail?.name}
              </Typography.Title>
              {gridStatusBudge}
            </div>
            <div className="w-1/2 pt-1">
              <Paragraph
                type="secondary"
                ellipsis
                editable={
                  disable
                    ? false
                    : {
                        onChange: (newValue) => {
                          if (newValue.trim() === reportDetailsPlaceholder) {
                            return;
                          }
                          setReportDetail((pre) =>
                            pre
                              ? {
                                  ...pre,
                                  description: newValue
                                }
                              : null
                          );
                          onUpdateProjectReport(
                            { description: newValue },
                            true
                          );
                        },
                        triggerType: ["text", "icon"]
                      }
                }
                className="m-0 text-sm"
                data-testid="custom-report-description"
              >
                {reportDetail?.description || reportDetailsPlaceholder}
              </Paragraph>
            </div>
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-start">
        <div className="flex items-center space-x-2 mt-2">
          {/* <Button icon={<MailOutlined />}>Email Alert</Button> */}
          {showExportOptions && (
            <Dropdown overlay={exportBtnMenu} placement="bottomRight">
              <Button
                type="primary"
                loading={isDownloadingExportedCSV}
                data-testid="custom-report-export-csv"
              >
                <Space>
                  Export CSV
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          )}

          {!showExportOptions && (
            <Button
              type="primary"
              loading={isDownloadingExportedCSV}
              data-testid="custom-report-export-csv"
              onClick={() => onExportCSV(ExportCSVFormat.FORMATTED)}
            >
              Export CSV
            </Button>
          )}

          {/* <Popover placement="left" content={<div>Help Content</div>}>
            <QuestionCircleOutlined className="pl-2 text-gray-600 hover:text-gray-800 cursor-pointer" />
          </Popover> */}
        </div>
      </div>
    </div>
  );
}

export default ReportsPageHeader;
